import {
  Button,
  Col,
  Container,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import SearchableSingleSelectDropdown from "../../../../common/SearchableDropdown";
import React, { useEffect, useState } from "react";
import {
  addPreference,
  fetchPreferenceDetails,
  fetchPreferenceDOSList,
  fetchPRferenceQMEList,
  updatePreference,
} from "../../../../services/PreferenceTabApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import Snackbar from "../../../../common/Snackbar";
import moment from "moment";
import OverlayDrawer from "../../../../common/OverlayDrawer";
import DataTable from "../../../../common/DataTable";
import { SUBJECT_LOG_HEADERS } from "../../Case_Subject/actions/constant";
import AccordionComponent from "../../../../common/Accordion";
import { useIsStageReadOnly } from "../../../../utils/caseStageChecker";
import { WEB_ROLE } from "../../../../common/Constant";

interface PreferenceProps {
  userRole: string;
}

const Preferences: React.FC<PreferenceProps> = ({ userRole }) => {
  const caseInfoID = useSelector(
    (state: RootState) => state.caseHeader.caseInfoId
  );
  const { view_Case } = useSelector((state: RootState) => state.featureAccess);
  const isStageNonEditable = useIsStageReadOnly();
  const [sendRecordsOption, setSendRecordsOption] = useState<any>([]);
  const [sendRecordTo, setSendRecordTo] = useState<string | number | null>(
    null
  );
  const [dosOptions, setDOSOptions] = useState<any>([]);
  const [dos, setDOS] = useState<string | number | null>(null);
  const [signatoryOptions, setSignatoryOptions] = useState<any>([]);
  const [signatoryQME, setSignatoryQME] = useState<string | number | null>(
    null
  );
  const [preferenceID, setPreferenceID] = useState<string | number | null>(
    null
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [preferenceLog, setPreferenceLog] = useState<any[]>([]);
  const [showLogDrawer, setShowLogDrawer] = useState(false);
  const [showLogIcon, setShowLogIcon] = useState(false);

  const [initialData, setInitialData] = useState({
    sendRecordTo: null,
    dos: null,
    signatoryQME: null,
  });

  // Fetch DOS Data (Records & QME Form options)
  const fetchDOSData = async () => {
    try {
      const recordsData = await fetchPreferenceDOSList(caseInfoID);
      setSendRecordsOption(recordsData || []);
      setDOSOptions(recordsData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Fetch QME List based on selected `addressType`
  const fetchQMEList = async (addressType: string) => {
    try {
      const qmeData = await fetchPRferenceQMEList(caseInfoID, addressType);
      setSignatoryOptions(qmeData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Handle DOS selection change
  const handleDOSChange = (
    e: any,
    name: string,
    value: string | number | null
  ) => {
    setDOS(value);

    // Find the selected DOS option
    const selectedOption = dosOptions.find(
      (option: any) => option.value === value
    );

    // If a valid option is selected, fetch QME list using its `addressType`
    if (selectedOption) {
      setSignatoryQME("");
      fetchQMEList(selectedOption.addressType);
    }
  };

  const fetchPreferenceData = async () => {
    try {
      const preferenceData = await fetchPreferenceDetails(caseInfoID);

      if (preferenceData) {
        setShowLogIcon(true);
        const initialSendRecordTo = preferenceData.sendRecordsToId || "";
        const initialDOS = preferenceData.qmeFormDeclarationOfServiceId || "";
        const initialSignatory = preferenceData.qmeFormSignatoryId || "";

        setPreferenceID(preferenceData.caseInfoPrefId || "");
        setSendRecordTo(initialSendRecordTo);
        setDOS(initialDOS);

        // Set initial values
        setInitialData({
          sendRecordTo: preferenceData.sendRecordsToId || "",
          dos: preferenceData.qmeFormDeclarationOfServiceId || "",
          signatoryQME: preferenceData.qmeFormSignatoryId || "",
        });

        const formattedCreatedDate = preferenceData.createdDate
          ? moment(preferenceData.createdDate).format("MM/DD/YYYY | HH:mm")
          : "N/A";

        const formattedModifiedDate = preferenceData.modifiedDate
          ? moment(preferenceData.modifiedDate).format("MM/DD/YYYY | HH:mm")
          : "N/A";

        // Store log details in state
        setPreferenceLog([
          {
            createdbyName: preferenceData.createdByName || "N/A",
            createddate: formattedCreatedDate,
            modifiedbyName: preferenceData.modifiedByName || "N/A",
            modifieddate: formattedModifiedDate,
          },
        ]);

        // If DOS is set, fetch QME List options
        if (preferenceData.qmeFormDeclarationOfServiceId) {
          const selectedOption = dosOptions.find(
            (option: any) =>
              option.value === preferenceData.qmeFormDeclarationOfServiceId
          );

          if (selectedOption) {
            await fetchQMEList(selectedOption.addressType); // Fetch signatory options

            // Wait for signatoryOptions to be updated before setting signatoryQME
            setTimeout(() => {
              setSignatoryQME(preferenceData.qmeFormSignatoryId || "");
            }, 500); // Small delay to ensure options are populated first
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchDOSData();
  }, [caseInfoID]);

  useEffect(() => {
    if (dosOptions.length > 0) {
      fetchPreferenceData();
    }
  }, [dosOptions, caseInfoID]);

  const handleSaveInfo = async () => {
    const Addpayload = {
      caseInfoId: caseInfoID,
      sendRecordsToId: sendRecordTo,
      qmeFormDeclarationOfServiceId: dos,
      qmeFormSignatoryId: signatoryQME,
    };

    const Updatepayload = {
      caseInfoPrefId: preferenceID,
      caseInfoId: caseInfoID,
      sendRecordsToId: sendRecordTo,
      qmeFormDeclarationOfServiceId: dos,
      qmeFormSignatoryId: signatoryQME,
    };

    let savePrference;
    if (!preferenceID) {
      savePrference = await addPreference(Addpayload);
    } else {
      savePrference = await updatePreference(Updatepayload);
    }

    if (savePrference && savePrference.isSuccess) {
      setSnackbarMessage(
        preferenceID
          ? "Preference details updated successfully."
          : "Preference details added successfully."
      );
      setError("success");
      setSnackbarOpen(true);
      fetchPreferenceData();
    } else {
      setSnackbarMessage("Failed to add Preference");
      setError("error");
      setSnackbarOpen(true);
    }
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const handlePDFClick = () => {
    setPreferenceLog(preferenceLog); //

    setShowLogDrawer(true);
    setShowTooltip(false);
  };

  const handleCloseLogDrawer = () => {
    setShowLogDrawer(false);
  };

  const hasChanges = () => {
    return (
      initialData.sendRecordTo !== sendRecordTo ||
      initialData.dos !== dos ||
      initialData.signatoryQME !== signatoryQME
    );
  };

  const isSaveDisabled = !sendRecordTo || !dos || !signatoryQME;

  const [recordpreferenceDetailKey, setrecordpreferenceDetailKey] = useState<
    string | null
  >("0");

  const handlePreferenceAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (recordpreferenceDetailKey === key) {
      return; // do nothing
    }
    setrecordpreferenceDetailKey(key || "0");
  };

  const recordpreferencedetailAccordion = (
    items: { title: string; content: JSX.Element }[]
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={recordpreferenceDetailKey}
      onSelect={handlePreferenceAccordionSelect}
    />
  );

  const allowedRoles = [
    WEB_ROLE.App_Admin,
    WEB_ROLE.Case_Manager,
    WEB_ROLE.IndependentQMEDoctor,
    WEB_ROLE.QME_DoctorPMC,
    WEB_ROLE.Practice_Manager,
    WEB_ROLE.Medical_Assistant,
    WEB_ROLE.Supervisor,
  ];

  const canEditCase = allowedRoles.includes(userRole);

  return (
    <Container fluid className="pb-5  InjurySubAccord">
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error} // Adjust the severity as needed
      />

      {recordpreferencedetailAccordion([
        {
          title: "QME Appointment Notification",
          content: (
            <>
              <Row className="mt-1">
                <Col sm={12} className="selection_dropdown">
                  <SearchableSingleSelectDropdown
                    label="Send Records To"
                    name="sendRecords"
                    required
                    options={sendRecordsOption}
                    value={sendRecordTo}
                    onChange={(e, name, value) => setSendRecordTo(value)}
                    disabled={!canEditCase && (view_Case || isStageNonEditable)}
                  />
                </Col>
              </Row>

              <Row className="mt-3">
                <Col sm={12} className="selection_dropdown">
                  <SearchableSingleSelectDropdown
                    label="QME Form- Declaration of Service"
                    name="qmeForm"
                    required
                    options={dosOptions}
                    value={dos}
                    onChange={handleDOSChange} // Updated handler
                    disabled={!canEditCase && (view_Case || isStageNonEditable)}
                  />
                </Col>
              </Row>

              {/* Adding border-bottom after the third dropdown */}
              <Row className="mt-3 ">
                <Col sm={12} className="selection_dropdown">
                  <SearchableSingleSelectDropdown
                    label="QME Form - Signatory"
                    name="signatory"
                    required
                    options={signatoryOptions}
                    value={signatoryQME}
                    onChange={(e, name, value) => setSignatoryQME(value)}
                    disabled={!canEditCase && (view_Case || isStageNonEditable)}
                  />
                </Col>
              </Row>
            </>
          ),
        },
      ])}

      {/* Buttons Row */}
      <Row className="mt-3">
        <Col sm={12} className="d-flex  align-items-center">
          <div>
            {/* Left Side - Logs Button */}
            {showLogIcon ? (
              <OverlayTrigger
                show={showTooltip}
                placement="auto"
                trigger={["hover", "focus"]} // Important to prevent sticky tooltip
                delay={{ show: 200, hide: 100 }}
                overlay={
                  <Tooltip style={{ backgroundColor: "#b3e0ff" }}>Logs</Tooltip>
                }
              >
                <Button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handlePDFClick}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <span className="material-icons">description</span>
                </Button>
              </OverlayTrigger>
            ) : null}
          </div>

          {/* Right Side - Action Buttons */}

          <div className="d-flex ms-auto">
            <Button
              variant="outline-info"
              className="d-flex align-items-center"
              onClick={async () => {
                setSendRecordTo(initialData.sendRecordTo);
                setDOS(initialData.dos);

                const selectedOption = dosOptions.find(
                  (option: any) => option.value === initialData.dos
                );

                if (selectedOption) {
                  await fetchQMEList(selectedOption.addressType); // Fetch correct signatory options
                  setSignatoryQME(initialData.signatoryQME);
                } else {
                  setSignatoryQME(""); // fallback
                }
              }}
              disabled={!canEditCase && (view_Case || isStageNonEditable)}
            >
              <span className="material-icons mr-2">close</span>Cancel
            </Button>
            {canEditCase && (
              <Button
                type="submit"
                variant="primary"
                className="ms-3 d-flex align-items-center"
                disabled={
                  !canEditCase &&
                  (isSaveDisabled ||
                    !hasChanges() ||
                    view_Case ||
                    isStageNonEditable)
                }
                onClick={handleSaveInfo}
              >
                <span className="material-icons mx-1">save</span>
                Save
              </Button>
            )}
          </div>
        </Col>
      </Row>
      <OverlayDrawer
        title={"Logs"}
        open={showLogDrawer}
        onClose={handleCloseLogDrawer}
        placement="bottom"
      >
        <DataTable
          headers={SUBJECT_LOG_HEADERS}
          rows={preferenceLog}
          isLogTable={true}
        />
      </OverlayDrawer>
    </Container>
  );
};

export default Preferences;
